import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Rating,
  Divider,
  Tab,
  Tabs,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  MoreVert,
  Visibility,
  VisibilityOff,
  Delete,
  Check,
  Close,
  Report,
  Person,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { generateClient } from 'aws-amplify/api';
import { isAdmin } from '../../utils/adminUtils';

const API_BASE_URL = "https://hype-circle-app-cloud-913961422896.europe-west1.run.app/api";

const client = generateClient();

const ReviewModerationPage = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [activeTab, setActiveTab] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: '', review: null });
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (isAdmin(user)) {
      fetchReviews();
      fetchReports();
    }
  }, [user]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      
      const [movieReviewsResult, showReviewsResult] = await Promise.all([
        client.graphql({
          query: `
            query ListMovieReviews {
              listMovieReviews(limit: 100) {
                items {
                  id
                  movieID
                  authorID
                  rating
                  review
                  status
                  createdAt
                }
              }
            }
          `,
        }),
        client.graphql({
          query: `
            query ListShowReviews {
              listShowReviews(limit: 100) {
                items {
                  id
                  showID
                  authorID
                  rating
                  review
                  status
                  createdAt
                }
              }
            }
          `,
        })
      ]);
      
      const movieReviews = movieReviewsResult.data.listMovieReviews.items || [];
      const showReviews = showReviewsResult.data.listShowReviews.items || [];
      
      // Enrich reviews with author and content data
      const enrichedMovieReviews = await Promise.all(
        movieReviews.map(async (review) => {
          try {
            const [authorResult, movieResult] = await Promise.all([
              client.graphql({
                query: `query GetUser($id: ID!) { getUser(id: $id) { id displayName username avatar } }`,
                variables: { id: review.authorID }
              }).then(result => {
                console.log('Author fetch result for', review.authorID, ':', result);
                return result;
              }).catch(error => {
                console.warn('Failed to fetch author', review.authorID, ':', error);
                return { data: { getUser: null } };
              }),
              // Fetch movie from MongoDB API
              fetch(`${API_BASE_URL}/movies/${review.movieID}`)
                .then(res => res.ok ? res.json() : null)
                .then(data => ({
                  data: {
                    getMovie: data ? {
                      id: data._id,
                      name: data.name,
                      poster: data.poster
                    } : null
                  }
                }))
                .catch(() => ({ data: { getMovie: null } }))
            ]);
            
            return {
              ...review,
              author: authorResult.data.getUser,
              movie: movieResult.data.getMovie,
              type: 'movie'
            };
          } catch (error) {
            console.warn('Error fetching related data for movie review:', error);
            return { ...review, type: 'movie' };
          }
        })
      );
      
      const enrichedShowReviews = await Promise.all(
        showReviews.map(async (review) => {
          try {
            const [authorResult, showResult] = await Promise.all([
              client.graphql({
                query: `query GetUser($id: ID!) { getUser(id: $id) { id displayName username avatar } }`,
                variables: { id: review.authorID }
              }).then(result => {
                console.log('Author fetch result for', review.authorID, ':', result);
                return result;
              }).catch(error => {
                console.warn('Failed to fetch author', review.authorID, ':', error);
                return { data: { getUser: null } };
              }),
              // Fetch show from MongoDB API
              fetch(`${API_BASE_URL}/shows/${review.showID}`)
                .then(res => res.ok ? res.json() : null)
                .then(data => ({
                  data: {
                    getShow: data ? {
                      id: data._id,
                      name: data.name,
                      poster: data.poster
                    } : null
                  }
                }))
                .catch(() => ({ data: { getShow: null } }))
            ]);
            
            return {
              ...review,
              author: authorResult.data.getUser,
              show: showResult.data.getShow,
              type: 'show'
            };
          } catch (error) {
            console.warn('Error fetching related data for show review:', error);
            return { ...review, type: 'show' };
          }
        })
      );
      
      const allReviews = [...enrichedMovieReviews, ...enrichedShowReviews]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setReviews(allReviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      addNotification({
        type: 'error',
        message: 'Failed to load reviews',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const result = await client.graphql({
        query: `
          query ListReviewReports {
            listReviewReports(limit: 100) {
              items {
                id
                reviewID
                reviewType
                reportedBy
                reason
                description
                status
                createdAt
              }
            }
          }
        `,
      });
      
      const reviewReports = result.data.listReviewReports.items || [];
      
      // Enrich reports with reporter and review data
      const enrichedReports = await Promise.all(
        reviewReports.map(async (report) => {
          try {
            const reporterResult = await client.graphql({
              query: `query GetUser($id: ID!) { getUser(id: $id) { id displayName username } }`,
              variables: { id: report.reportedBy }
            }).then(result => {
              console.log('Reporter fetch result for', report.reportedBy, ':', result);
              return result;
            }).catch(error => {
              console.warn('Failed to fetch reporter', report.reportedBy, ':', error);
              return { data: { getUser: null } };
            });
            
            // Fetch the actual review based on type
            let reviewResult;
            if (report.reviewType === 'MOVIE') {
              reviewResult = await client.graphql({
                query: `query GetMovieReview($id: ID!) { getMovieReview(id: $id) { id rating review authorID movieID } }`,
                variables: { id: report.reviewID }
              });
            } else {
              reviewResult = await client.graphql({
                query: `query GetShowReview($id: ID!) { getShowReview(id: $id) { id rating review authorID showID } }`,
                variables: { id: report.reviewID }
              });
            }
            
            return {
              ...report,
              reporter: reporterResult.data.getUser,
              review: reviewResult.data.getMovieReview || reviewResult.data.getShowReview
            };
          } catch (error) {
            console.warn('Error fetching related data for review report:', error);
            return report;
          }
        })
      );
      
      setReports(enrichedReports);
    } catch (error) {
      console.error('Error fetching review reports:', error);
      addNotification({
        type: 'error',
        message: 'Failed to load review reports',
      });
    }
  };

  const handleReviewAction = async (reviewId, action) => {
    try {
      const review = reviews.find(r => r.id === reviewId);
      if (!review) return;
      
      const mutation = review.type === 'movie' ? 'updateMovieReview' : 'updateShowReview';
      const input = review.type === 'movie' ? 'UpdateMovieReviewInput' : 'UpdateShowReviewInput';
      
      let status;
      switch (action) {
        case 'approve': status = 'APPROVED'; break;
        case 'reject': status = 'REJECTED'; break;
        case 'hide': status = 'HIDDEN'; break;
        case 'delete':
          // Delete the review
          const deleteQuery = review.type === 'movie' ? 'deleteMovieReview' : 'deleteShowReview';
          const deleteInput = review.type === 'movie' ? 'DeleteMovieReviewInput' : 'DeleteShowReviewInput';
          
          await client.graphql({
            query: `
              mutation DeleteReview($input: ${deleteInput}!) {
                ${deleteQuery}(input: $input) {
                  id
                }
              }
            `,
            variables: {
              input: { id: reviewId }
            }
          });
          
          setReviews(prev => prev.filter(r => r.id !== reviewId));
          addNotification({ 
            type: 'success', 
            message: 'Review deleted successfully' 
          });
          setActionDialog({ open: false, action: '', review: null });
          return;
        default: return;
      }
      
      await client.graphql({
        query: `
          mutation UpdateReview($input: ${input}!) {
            ${mutation}(input: $input) {
              id
              status
            }
          }
        `,
        variables: {
          input: { id: reviewId, status }
        }
      });
      
      setReviews(prev => prev.map(review => 
        review.id === reviewId 
          ? { ...review, status }
          : review
      ));

      addNotification({ 
        type: 'success', 
        message: `Review ${action.toLowerCase()}d successfully` 
      });
      
      setActionDialog({ open: false, action: '', review: null });
    } catch (error) {
      console.error('Error updating review:', error);
      addNotification({ type: 'error', message: 'Failed to update review' });
    }
  };

  const handleReportAction = async (reportId, action) => {
    try {
      const status = action.toUpperCase();
      
      await client.graphql({
        query: `
          mutation UpdateReviewReport($input: UpdateReviewReportInput!) {
            updateReviewReport(input: $input) {
              id
              status
            }
          }
        `,
        variables: {
          input: { id: reportId, status }
        }
      });
      
      setReports(prev => prev.map(report => 
        report.id === reportId 
          ? { ...report, status }
          : report
      ));

      addNotification({ 
        type: 'success', 
        message: `Report ${action.toLowerCase()}d successfully` 
      });
    } catch (error) {
      console.error('Error updating report:', error);
      addNotification({ type: 'error', message: 'Failed to update report' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'error';
      case 'HIDDEN': return 'warning';
      case 'PENDING': return 'info';
      default: return 'default';
    }
  };

  const ReviewCard = ({ review }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 40, height: 40 }}>
              {review.author?.displayName?.[0] || 'U'}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {review.author?.displayName || review.author?.username || 'Anonymous'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {review.movie?.name || review.show?.name || `${review.type} (ID: ${review.movieID || review.showID})`}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={review.status} 
              color={getStatusColor(review.status)} 
              size="small" 
            />
            {reports.filter(r => r.reviewID === review.id).length > 0 && (
              <IconButton
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                  setSelectedReview(review);
                }}
              >
                <MoreVert />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Rating value={review.rating} readOnly size="small" />
          <Typography variant="caption" color="text.secondary">
            {new Date(review.createdAt).toLocaleDateString()}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 2 }}>
          {review.review}
        </Typography>

        {reports.filter(r => r.reviewID === review.id).length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="caption">
              {reports.filter(r => r.reviewID === review.id).length} report(s)
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  const ReportCard = ({ report }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" color="error">
              {report.reason}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Reported by {report.reporter?.displayName || report.reporter?.username || 'Unknown User'} • {new Date(report.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
          <Chip 
            label={report.status} 
            color={getStatusColor(report.status)} 
            size="small" 
          />
        </Box>

        {report.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {report.description}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Reported Review:
        </Typography>
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
          <Typography variant="body2">
            {report.review?.review}
          </Typography>
        </Box>

        {report.status === 'PENDING' && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<Check />}
              onClick={() => handleReportAction(report.id, 'resolved')}
            >
              Resolve
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<Close />}
              onClick={() => handleReportAction(report.id, 'dismissed')}
            >
              Dismiss
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (!isAdmin(user)) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Access denied. Admin privileges required.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Review Moderation
      </Typography>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label={`All Reviews (${reviews.length})`} />
        <Tab label={`Reported Reviews (${reports.length})`} />
        <Tab label="Pending Reviews" />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {reports.map((report) => (
            <ReportCard key={report.id} report={report} />
          ))}
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          {reviews.filter(r => r.status === 'PENDING').map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </Box>
      )}

      {/* Review Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && Boolean(selectedReview)}
        onClose={() => {
          setAnchorEl(null);
          setSelectedReview(null);
        }}
      >
        {selectedReview && (
          <>
            <MenuItem onClick={() => {
              setActionDialog({ open: true, action: 'approve', review: selectedReview });
              setAnchorEl(null);
              setSelectedReview(null);
            }}>
              <Check sx={{ mr: 1 }} /> Approve
            </MenuItem>
            <MenuItem onClick={() => {
              setActionDialog({ open: true, action: 'reject', review: selectedReview });
              setAnchorEl(null);
              setSelectedReview(null);
            }}>
              <Close sx={{ mr: 1 }} /> Reject
            </MenuItem>
            <MenuItem onClick={() => {
              setActionDialog({ open: true, action: 'hide', review: selectedReview });
              setAnchorEl(null);
              setSelectedReview(null);
            }}>
              <VisibilityOff sx={{ mr: 1 }} /> Hide
            </MenuItem>
            <MenuItem onClick={() => {
              setActionDialog({ open: true, action: 'delete', review: selectedReview });
              setAnchorEl(null);
              setSelectedReview(null);
            }}>
              <Delete sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, action: '', review: null })}
      >
        <DialogTitle>
          Confirm Action
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {actionDialog.action} this review?
          </Typography>
          {actionDialog.review && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2">
                "{actionDialog.review.review}"
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, action: '', review: null })}>
            Cancel
          </Button>
          <Button
            onClick={() => handleReviewAction(actionDialog.review?.id, actionDialog.action)}
            variant="contained"
            color={actionDialog.action === 'delete' ? 'error' : 'primary'}
          >
            {actionDialog.action}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReviewModerationPage;